import styled, { css, keyframes } from "styled-components";
import { ConversationIndividualType } from "./Data/ConversationData";
import { useEffect, useRef, useState } from "react";

const ImageKeyframes = keyframes`
    0%{
        transform: rotate(0deg);
    }50%{
        transform: rotate(0deg);
    }55%{
        transform: rotate(1deg);
    }60%{
        transform: rotate(0deg);
    }100%{
        transform: rotate(0deg);
    }
`;

const SlideKeyFramesLeftRight = keyframes`
    0%{
        opacity:0;
        transform: translateX(-80%);
    }100%{
        opacity:1;
        transform: translateX(0px);
    }
`;
const SlideKeyFramesRightLeft = keyframes`
    0%{
        opacity:0;
        transform: translateX(80%);
    }100%{
        opacity:1;
        transform: translateX(0px);
    }
`;

interface ConversationImageProps {
    $animationLength: number;
    $inView: boolean;
    $flipped: boolean;
}
// if flipped, transition from right to left
// else left to right
const ConversationImage = styled.img<ConversationImageProps>`
    border-radius: 10px;
    padding-bottom: 20px;
    ${({ theme, $animationLength }) => css`
        animation: ${ImageKeyframes} ${$animationLength}s ease;
        animation-iteration-count: infinite;
        @media screen and (max-width: ${theme.breakpoint}px) {
            width: 90vw;
        }
    `}
    ${({ $inView, $flipped }) =>
        $inView
            ? css`
                  animation: ${$flipped
                          ? SlideKeyFramesRightLeft
                          : SlideKeyFramesLeftRight}
                      1s ease;
                  animation-fill-mode: forwards;
                  animation-iteration-count: 1;
              `
            : css`
                  opacity: 0;
              `}
`;

const ConversationInfo = styled.div`
    display: flex;
    flex-direction: column;
    max-width: 500px;
    gap: 30px;
    border-radius: 10px;
    transform: translateY(-20px);
    ${({ theme }) => css`
        @media screen and (max-width: ${theme.breakpoint}px) {
            box-shadow: none;
            padding: 0 1rem;
        }
    `}
`;
interface ConversationInfoTitleProps {
    $flipFlow: boolean;
    $inView: boolean;
    $flipped: boolean;
}
// if flipped, transition from left to right
// else right to left
const ConversationInfoTitle = styled.div<ConversationInfoTitleProps>`
    font-weight: 600;
    ${({ theme, $flipFlow }) => css`
        font-size: ${theme.fontSize.large}px;
        @media screen and (max-width: ${theme.breakpoint}px) {
            font-size: ${theme.fontSize.medium}px;
        }
    `}
    ${({ $inView, $flipped }) =>
        $inView
            ? css`
                  animation: ${$flipped
                          ? SlideKeyFramesLeftRight
                          : SlideKeyFramesRightLeft}
                      1s ease;
                  animation-fill-mode: forwards;
                  animation-iteration-count: 1;
              `
            : css`
                  opacity: 0;
              `}
`;
interface ConversationInfoInfoProps {
    $inView: boolean;
    $flipped: boolean;
}
// if flipped, transition from left to right
// else right to left
const ConversationInfoInfo = styled.div<ConversationInfoInfoProps>`
    border-radius: 10px;
    font-weight: 300;
    ${({ theme }) => css`
        font-size: ${theme.fontSize.medium * 0.78}px;
    `}
    ${({ $inView, $flipped }) =>
        $inView
            ? css`
                  animation: ${$flipped
                          ? SlideKeyFramesLeftRight
                          : SlideKeyFramesRightLeft}
                      1s ease;
                  animation-fill-mode: forwards;
                  animation-iteration-count: 1;
              `
            : css`
                  opacity: 0;
              `}
`;
interface ConversationInfoNameProps {
    $inView: boolean;
    $flipped: boolean;
}
// if flipped, transition from left to right
// else right to left
const ConversationInfoNameIcon = styled.div<ConversationInfoNameProps>`
    display: flex;
    align-items: center;
    gap: 20px;
    ${({ $inView, $flipped }) =>
        $inView
            ? css`
                  animation: ${$flipped
                          ? SlideKeyFramesLeftRight
                          : SlideKeyFramesRightLeft}
                      1s ease;
                  animation-fill-mode: forwards;
                  animation-iteration-count: 1;
              `
            : css`
                  opacity: 0;
              `}
`;
const ConversationInfoName = styled.div`
    text-transform: uppercase;
`;
const ConversationInfoIcon = styled.img`
    width: 35px;
    height: 35px;
`;

interface ConversationContainerProps {
    $flipFlow: boolean;
}
const ConversationContainer = styled.div<ConversationContainerProps>`
    display: flex;
    align-items: center;
    width: 100vw;
    justify-content: space-evenly;
    gap: 20px;
    ${({ theme }) => css`
        @media screen and (max-width: ${theme.breakpoint}px) {
            flex-direction: column;
        }
    `}
    ${({ $flipFlow }) => css`
        flex-direction: ${$flipFlow ? "row-reverse" : "row"};
    `}
`;

const ConversationCardPropsDefault = {
    cardDataObj: {
        image: "",
        info: {
            icon: "",
            name: "default",
            title: "default",
            info: "default info",
        },
        flip: false,
    },
};

export const observerOptions: IntersectionObserverInit = {
    threshold: 0.15,
};

interface ConversationCardProps {
    cardDataObj?: ConversationIndividualType;
}

export const ConversationCard = ({
    cardDataObj = ConversationCardPropsDefault.cardDataObj,
}: ConversationCardProps) => {
    const [conversationImageInView, setConversationImageInView] =
        useState<boolean>(false);
    const [conversationInfoNameInView, setConversationInfoNameInView] =
        useState<boolean>(false);
    const [conversationInfoTitleInView, setConversationInfoTitleInView] =
        useState<boolean>(false);
    const [conversationInfoInfoInView, setConversationInfoInfoInView] =
        useState<boolean>(false);

    const conversationImageRef = useRef<HTMLImageElement>(null);
    const conversationInfoNameIconRef = useRef<HTMLDivElement>(null);
    const conversationInfoTitleRef = useRef<HTMLDivElement>(null);
    const conversationInfoInfoRef = useRef<HTMLDivElement>(null);

    const handleObservations = (entry: IntersectionObserverEntry) => {
        if (
            conversationImageRef.current &&
            conversationInfoNameIconRef.current &&
            conversationInfoTitleRef.current &&
            conversationInfoInfoRef.current
        ) {
            switch (entry.target) {
                case conversationImageRef.current: {
                    setConversationImageInView(entry.isIntersecting);
                    break;
                }
                case conversationInfoNameIconRef.current: {
                    setConversationInfoNameInView(entry.isIntersecting);
                    break;
                }
                case conversationInfoTitleRef.current: {
                    setConversationInfoTitleInView(entry.isIntersecting);
                    break;
                }
                case conversationInfoInfoRef.current: {
                    setConversationInfoInfoInView(entry.isIntersecting);
                    break;
                }
            }
        }
    };

    useEffect(() => {
        const observer: IntersectionObserver = new IntersectionObserver(
            (entries: IntersectionObserverEntry[]) => {
                console.log("remount");
                entries.forEach((entry) => handleObservations(entry));
            },
            observerOptions
        );
        if (
            conversationImageRef.current &&
            conversationInfoNameIconRef.current &&
            conversationInfoTitleRef.current &&
            conversationInfoInfoRef.current
        ) {
            const refs = [
                conversationImageRef.current,
                conversationInfoNameIconRef.current,
                conversationInfoTitleRef.current,
                conversationInfoInfoRef.current,
            ];
            refs.forEach((ref) => observer.observe(ref));
        }
        return () => observer.disconnect();
    }, [
        conversationImageRef,
        conversationInfoNameIconRef,
        conversationInfoTitleRef,
        conversationInfoInfoRef,
    ]);
    return (
        <ConversationContainer $flipFlow={cardDataObj.flip}>
            <ConversationImage
                $animationLength={Math.random() * (5 - 1) + 1}
                src={cardDataObj.image}
                ref={conversationImageRef}
                $inView={conversationImageInView}
                $flipped={cardDataObj.flip}
            />
            <ConversationInfo>
                <ConversationInfoNameIcon
                    ref={conversationInfoNameIconRef}
                    $inView={conversationInfoNameInView}
                    $flipped={cardDataObj.flip}
                >
                    <ConversationInfoIcon src={cardDataObj.info.icon} />
                    <ConversationInfoName>
                        {cardDataObj.info.name}
                    </ConversationInfoName>
                </ConversationInfoNameIcon>
                <ConversationInfoTitle
                    $flipFlow={cardDataObj.flip}
                    ref={conversationInfoTitleRef}
                    $inView={conversationInfoTitleInView}
                    $flipped={cardDataObj.flip}
                >
                    {cardDataObj.info.title}
                </ConversationInfoTitle>
                <ConversationInfoInfo
                    ref={conversationInfoInfoRef}
                    $inView={conversationInfoInfoInView}
                    $flipped={cardDataObj.flip}
                >
                    {cardDataObj.info.info}
                </ConversationInfoInfo>
            </ConversationInfo>
        </ConversationContainer>
    );
};
